import { memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageCircle, Repeat2, Eye, Check, ChevronDown, Heart } from 'lucide-react'
import { cn } from '@/lib/utils'

import { formatCompactNumber } from '../../../shared/lib/number'
import { formatTimestamp } from '../../../shared/lib/time'
import { type Post } from '../model/contracts'
import { getAuthorParts, getContextLabels } from '../utils/postPresentation'

import { useHighlightKeywords } from '../lib/useHighlightKeywords'
import { normalizePlatform, SOCIAL_PLATFORMS } from '../model/platforms'
import { useTagKeywordMap } from '../state/useTagKeywordMap'
import { PostCommentsContainer } from './PostComments'
import { SentimentBadge } from './SentimentBadge'
import { PostActionMenu } from './PostActionMenu'

const ICON_STROKE = 2

/** Small colored dot representing the platform */
function PlatformDot({ platform }: { platform: string }) {
  const p = normalizePlatform(platform)
  const color =
    p === SOCIAL_PLATFORMS.FACEBOOK
      ? 'bg-platform-facebook'
      : p === SOCIAL_PLATFORMS.X
        ? 'bg-platform-x'
        : p === SOCIAL_PLATFORMS.THREADS
          ? 'bg-platform-threads'
          : 'bg-platform-others'

  return <span className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', color)} />
}

export const PostCard = memo(function PostCard(props: { post: Post }) {
  const p = props.post
  const author = getAuthorParts(p.author)
  const contextLabels = getContextLabels(p.context)
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const { data: keywordMap } = useTagKeywordMap()

  const tagKeywords = useMemo(() => {
    if (!keywordMap || !p.tags) return []
    const kws: string[] = []
    for (const tag of p.tags) {
      const keywords = keywordMap.get(tag)
      if (keywords) kws.push(...keywords)
    }
    return kws
  }, [keywordMap, p.tags])

  const highlight = useHighlightKeywords(tagKeywords)
  const [commentsOpen, setCommentsOpen] = useState(false)

  const getPlatformLabel = (platform: string) => {
    return t(`platforms.${normalizePlatform(platform)}`)
  }

  const renderContent = () => {
    const content = p.content || ''

    if (!content) {
      return (
        <div className="text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
          {t('posts.noContent')}
        </div>
      )
    }

    const highlighted = highlight(content)

    if (isExpanded) {
      return (
        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground sm:text-[15px]">
          {highlighted}
        </div>
      )
    }

    return (
      <div className="relative">
        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground line-clamp-4 sm:text-[15px] sm:line-clamp-6">
          {highlighted}
        </div>
        {content.length > 280 && (
          <button
            onClick={() => setIsExpanded(true)}
            className="mt-1 flex items-center gap-0.5 text-xs font-medium text-primary transition-colors hover:text-primary/80 sm:text-sm"
          >
            {t('posts.readMore')}
            <ChevronDown size={12} />
          </button>
        )}
      </div>
    )
  }

  return (
    <article className="post-card group relative min-w-0 rounded-2xl border border-border bg-card p-3 shadow-sm transition-[transform,box-shadow,border-color] duration-200 lg:hover:scale-[1.01] lg:hover:border-border-hover lg:hover:shadow-[0_4px_6px_-1px_var(--shadow-brand-highlight),0_2px_4px_-2px_var(--shadow-brand-highlight)] dark:shadow-none sm:p-5 [contain:layout_style_paint]">
      {/* Header: avatar + name/platform/time + external link */}
      <header className="mb-3 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-medium text-foreground sm:h-10 sm:w-10 sm:text-lg">
          {author.name.charAt(0).toUpperCase()}
        </div>

        <div className="min-w-0 flex-1">
          {/* Single-line: name · platform dot · timestamp */}
          <div className="flex items-center gap-1.5 text-sm sm:gap-2">
            <span className="truncate font-semibold text-foreground">{author.name}</span>
            {author.verified && (
              <span title={t('posts.verified')} className="flex shrink-0 text-primary">
                <Check size={13} strokeWidth={3} />
              </span>
            )}
            <PlatformDot platform={p.platform} />
            <span
              className="hidden text-xs text-muted-foreground sm:inline"
              title={getPlatformLabel(p.platform)}
            >
              {getPlatformLabel(p.platform)}
            </span>
            <span className="text-muted-foreground">·</span>
            <time
              dateTime={p.timestamp}
              className="shrink-0 text-xs text-muted-foreground"
            >
              {formatTimestamp(p.timestamp)}
            </time>
            {p.sentiment && <SentimentBadge sentiment={p.sentiment} className="ml-1" />}
          </div>

          {/* Context labels (if any) — second line, compact */}
          {contextLabels.length > 0 && (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {contextLabels.map((label) => (
                <span
                  key={label}
                  className="rounded-md bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>

        {p.url && <PostActionMenu post={p} />}
      </header>

      {/* Content */}
      <div className="mb-3">{renderContent()}</div>

      {p.tags && p.tags.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {p.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-chip-active-surface px-2 py-px text-xs font-medium text-chip-active ring-1 ring-chip-active-ring"
            >
              <span className="opacity-50">#</span>
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Metrics footer */}
      <div className="flex items-center gap-1 border-t border-border pt-3">
        <MetricButton
          icon={Heart}
          count={p.metrics.reactions}
          label={t('metrics.reactions')}
          variant="reactions"
        />
        <MetricButton
          icon={MessageCircle}
          count={p.metrics.comments}
          label={t('metrics.comments')}
          variant="comments"
          onClick={() => setCommentsOpen((v) => !v)}
        />
        <MetricButton
          icon={Repeat2}
          count={p.metrics.shares}
          label={t('metrics.shares')}
          variant="shares"
        />
        <MetricButton
          icon={Eye}
          count={p.metrics.views}
          label={t('metrics.views')}
          variant="views"
        />
      </div>

      {commentsOpen && (
        <PostCommentsContainer postId={p.id} onCollapse={() => setCommentsOpen(false)} />
      )}
    </article>
  )
})

type MetricVariant = 'reactions' | 'comments' | 'shares' | 'views'

type MetricButtonProps = {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>
  count: number
  label: string
  variant?: MetricVariant
  onClick?: () => void
}

const METRIC_STYLES: Record<MetricVariant, string> = {
  reactions:
    'hover:bg-metric-reactions/8 hover:text-metric-reactions dark:hover:bg-metric-reactions/10',
  comments:
    'hover:bg-metric-comments/8 hover:text-metric-comments dark:hover:bg-metric-comments/10',
  shares:
    'hover:bg-metric-shares/8 hover:text-metric-shares dark:hover:bg-metric-shares/10',
  views: 'hover:bg-metric-views/8 hover:text-metric-views dark:hover:bg-metric-views/10',
}

const MetricButton = memo(function MetricButton({
  icon: Icon,
  count,
  label,
  variant = 'views',
  onClick,
}: MetricButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group inline-flex min-h-[44px] cursor-pointer items-center gap-1.5 rounded-full px-3 py-2 text-xs text-muted-foreground transition-all duration-150 active:scale-95 sm:text-sm',
        METRIC_STYLES[variant],
      )}
      aria-label={`${label}: ${formatCompactNumber(count)}`}
    >
      <Icon size={15} strokeWidth={ICON_STROKE} className="shrink-0" />
      <span className="tabular-nums">{formatCompactNumber(count)}</span>
    </button>
  )
})
